import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { 
  MessageCircle, 
  Instagram, 
  MessagesSquare, 
  Send, 
  Globe,
  MoreVertical,
  Settings,
  Trash2,
  Power,
  PowerOff,
  QrCode,
  RefreshCw,
  Webhook,
  Phone,
  Wifi,
  WifiOff,
  Loader2,
  AlertCircle,
  Unplug,
  Clock,
  PauseCircle,
  Star,
  ArrowRightLeft,
  Server,
  Link2,
  Unlink,
  Eye
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatInboxIdentifierForDisplay } from '@/lib/phone-utils';
import { ChannelType, InboxStatus, PendingMigrationInfo } from '@/components/tenant/InboxCard';

/**
 * Interface para instância vinculada à inbox
 */
interface LinkedInstance {
  id: string;
  instance_name: string;
  status: string;
  provider_display_name?: string;
}

/**
 * Interface para inbox com dados do provider e instância
 */
interface Inbox {
  id: string;
  name: string;
  channel_type: ChannelType;
  identifier: string;
  status: InboxStatus;
  is_default?: boolean;
  provider?: {
    name: string;
    display_name?: string;
    logo_url?: string;
    slug?: string; // Slug do provider para identificação (ex: meta-whatsapp)
  };
  provider_version?: {
    version: string;
    status: string;
  };
  /** Instância vinculada à inbox */
  linked_instance?: LinkedInstance | null;
}

/**
 * Props do componente InboxListRow
 * Reutiliza mesmas props do InboxCard para consistência
 */
interface InboxListRowProps {
  inbox: Inbox;
  onEdit: (inbox: Inbox) => void;
  onDelete: (inbox: Inbox) => void;
  onToggleStatus: (inbox: Inbox) => void;
  onConnect?: (inbox: Inbox) => void;
  onCheckStatus?: (inbox: Inbox) => void;
  onConfigureWebhooks?: (inbox: Inbox) => void;
  onDisconnect?: (inbox: Inbox) => void;
  onSetDefault?: (inbox: Inbox) => void;
  onConfigureWebchat?: (inbox: Inbox) => void;
  /** Callback para conexão OAuth (WhatsApp Oficial) */
  onConnectOAuth?: (inbox: Inbox) => void;
  /** Callback para desvincular instância */
  onUnlinkInstance?: (inbox: Inbox) => void;
  /** Callback para vincular instância */
  onLinkInstance?: (inbox: Inbox) => void;
  /** Callback para abrir o modal de detalhes da inbox */
  onViewDetails?: (inbox: Inbox) => void;
  canEditInbox?: boolean;
  canDeleteInbox?: boolean;
  /** Indica se há migração pendente para esta inbox */
  hasPendingMigration?: boolean;
  /** Informações da migração pendente */
  migrationInfo?: PendingMigrationInfo;
}

// Configuração de ícones e labels por canal
const channelConfig: Record<ChannelType, { icon: typeof MessageCircle; label: string }> = {
  whatsapp: { icon: MessageCircle, label: 'WhatsApp' },
  whatsapp_oficial: { icon: MessageCircle, label: 'WhatsApp Oficial' },
  instagram: { icon: Instagram, label: 'Instagram' },
  messenger: { icon: MessagesSquare, label: 'Messenger' },
  telegram: { icon: Send, label: 'Telegram' },
  webchat: { icon: Globe, label: 'WebChat' },
};

// Cores dos ícones por canal
const channelIconColors: Record<ChannelType, string> = {
  whatsapp: 'text-green-600 dark:text-green-400',
  whatsapp_oficial: 'text-emerald-600 dark:text-emerald-400',
  instagram: 'text-pink-600 dark:text-pink-400',
  messenger: 'text-blue-600 dark:text-blue-400',
  telegram: 'text-sky-500 dark:text-sky-400',
  webchat: 'text-muted-foreground',
};

// Cores de fundo dos ícones por canal
const channelBgColors: Record<ChannelType, string> = {
  whatsapp: 'bg-green-500/10',
  whatsapp_oficial: 'bg-emerald-500/10',
  instagram: 'bg-gradient-to-br from-pink-500/10 to-purple-500/10',
  messenger: 'bg-blue-500/10',
  telegram: 'bg-sky-400/10',
  webchat: 'bg-muted',
};

// Estilos de status - inclui pending_setup para OAuth WhatsApp Oficial
const statusStyles: Record<InboxStatus, string> = {
  pending: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
  pending_setup: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  active: 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20',
  inactive: 'bg-muted text-muted-foreground border-muted',
  connecting: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  error: 'bg-destructive/10 text-destructive border-destructive/20',
  disconnected: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20',
  suspended: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
};

// Cores apenas do ícone de status (usadas quando o badge é simplificado para ícone + texto preto)
const statusIconColors: Record<InboxStatus, string> = {
  pending: 'text-amber-600 dark:text-amber-400',
  pending_setup: 'text-blue-600 dark:text-blue-400',
  active: 'text-green-600 dark:text-green-400',
  inactive: 'text-muted-foreground',
  connecting: 'text-blue-600 dark:text-blue-400',
  error: 'text-destructive',
  disconnected: 'text-orange-600 dark:text-orange-400',
  suspended: 'text-purple-600 dark:text-purple-400',
};

// Ícones de status - inclui pending_setup
const statusIcons: Record<InboxStatus, typeof Wifi> = {
  pending: Clock,
  pending_setup: Clock,
  active: Wifi,
  inactive: WifiOff,
  connecting: Loader2,
  error: AlertCircle,
  disconnected: WifiOff,
  suspended: PauseCircle,
};

// Animação dos ícones de status - inclui pending_setup
const statusIconAnimation: Record<InboxStatus, string> = {
  pending: '',
  pending_setup: '',
  active: '',
  inactive: '',
  connecting: 'animate-spin',
  error: '',
  disconnected: '',
  suspended: '',
};


// Labels de status - inclui pending_setup para OAuth WhatsApp Oficial
const statusLabels: Record<InboxStatus, string> = {
  pending: 'Pendente',
  pending_setup: 'Aguardando Ativação',
  active: 'Conectado',
  inactive: 'Inativo',
  connecting: 'Conectando...',
  error: 'Erro',
  disconnected: 'Desconectado',
  suspended: 'Suspensa',
};


/**
 * Componente de linha para exibição de inbox em formato lista
 * Exibe informações em colunas horizontais com ações no final
 */
export function InboxListRow({ 
  inbox, 
  onEdit, 
  onDelete, 
  onToggleStatus, 
  onConnect, 
  onCheckStatus, 
  onConfigureWebhooks,
  onDisconnect,
  onSetDefault,
  onConfigureWebchat,
  onConnectOAuth,
  onUnlinkInstance,
  onLinkInstance,
  onViewDetails,
  canEditInbox = true,
  canDeleteInbox = true,
  hasPendingMigration = false,
  migrationInfo,
}: InboxListRowProps) {
  const navigate = useNavigate();
  // Fallbacks seguros para evitar crash com channel_type ou status desconhecido
  const channel = channelConfig[inbox.channel_type] || channelConfig.whatsapp;
  const ChannelIcon = channel.icon;
  const StatusIcon = statusIcons[inbox.status] || statusIcons.pending;
  const iconColor = channelIconColors[inbox.channel_type] || channelIconColors.whatsapp;
  const bgColor = channelBgColors[inbox.channel_type] || channelBgColors.whatsapp;
  const statusStyle = statusStyles[inbox.status] || statusStyles.pending;
  const statusLabel = statusLabels[inbox.status] || 'Desconhecido';
  const statusAnim = statusIconAnimation[inbox.status] || '';
  
  // WhatsApp não-oficial requer conexão QR
  const isWhatsAppNonOfficial = inbox.channel_type === 'whatsapp';
  // WhatsApp Oficial requer OAuth
  const isWhatsAppOficial = inbox.channel_type === 'whatsapp_oficial';
  // Provider Meta WABA permite reconexão via Embedded Signup
  const isMetaWhatsApp = inbox.provider?.slug === 'meta-whatsapp';
  // WebChat tem configurações específicas
  const isWebchat = inbox.channel_type === 'webchat';
  // Inbox inativa ou suspensa não pode executar ações de conexão
  const isInactive = inbox.status === 'inactive';
  const isSuspended = inbox.status === 'suspended';
  // Conexão OAuth para WhatsApp Oficial (status pending ou disconnected)
  const needsOAuthConnection = isWhatsAppOficial && ['pending', 'disconnected'].includes(inbox.status);
  // Verifica se tem instância vinculada
  const hasLinkedInstance = !!inbox.linked_instance;
  
  // Ações de provider (QR, status, webhooks) habilitadas para status operacionais
  // Status permitidos: active, pending, connecting, error, disconnected
  const canExecuteProviderActions = 
    ['active', 'pending', 'connecting', 'error', 'disconnected'].includes(inbox.status) && 
    hasLinkedInstance;

  // Handler para abrir o modal de detalhes (fallback: rota antiga)
  const handleViewDetails = () => {
    if (onViewDetails) {
      onViewDetails(inbox);
      return;
    }
    navigate(`/tenant/inboxes/${inbox.id}`);
  };

  return (
    <div className="flex items-center gap-2 sm:gap-4 px-2 py-2 sm:px-4 sm:py-3 hover:bg-muted/50 transition-colors">
      {/* Ícone do Canal - Responsivo */}
      <div className={cn(
        "p-1.5 sm:p-2 rounded-lg shrink-0",
        bgColor
      )}>
        <ChannelIcon className={cn("h-3.5 w-3.5 sm:h-4 sm:w-4", iconColor)} />
      </div>

      {/* Nome e Tipo de Canal + Código + Badge Padrão - Responsivo */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1 sm:gap-2">
          <p className="font-medium text-xs sm:text-sm truncate">{inbox.name}</p>
          {inbox.is_default && (
            <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4 bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20 shrink-0">
              <Star className="h-2.5 w-2.5 mr-0.5 fill-current" />
            </Badge>
          )}
        </div>
        <p className="text-[10px] sm:text-xs text-muted-foreground truncate">
          {channel.label}
          {/* Mobile: exibe o código inline pois a coluna dedicada fica oculta */}
          <span className="sm:hidden font-mono ml-1">· {inbox.id.substring(0, 8).toUpperCase()}</span>
        </p>
      </div>

      {/* Coluna Cód. Inbox dedicada (facilita identificação no suporte) */}
      <div className="hidden sm:flex items-center w-24 shrink-0">
        <Badge
          variant="outline"
          className="font-mono text-[10px] px-1.5 py-0 h-5 text-muted-foreground border-muted-foreground/30"
        >
          {inbox.id.substring(0, 8).toUpperCase()}
        </Badge>
      </div>

      {/* Identificador - formatado para exibição */}
      <div className="hidden sm:flex items-center gap-1.5 w-40 shrink-0">
        <Phone className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <span className="font-mono text-xs text-muted-foreground truncate">
          {formatInboxIdentifierForDisplay(inbox.channel_type, inbox.identifier)}
        </span>
      </div>

      {/* Instância Vinculada - apenas ícone colorido + texto em cor padrão (sem badge) */}
      <div className="hidden md:flex items-center gap-2 w-32 shrink-0">
        {hasLinkedInstance ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1.5 text-xs text-foreground">
                  <Server className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                  <span className="truncate">
                    {inbox.linked_instance?.instance_name?.substring(0, 12) || 'Instância'}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p className="font-medium">{inbox.linked_instance?.instance_name}</p>
                <p className="text-xs text-muted-foreground">
                  {inbox.linked_instance?.provider_display_name || 'Provider'}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : !isWebchat && (
          <div className="flex items-center gap-1.5 text-xs text-foreground">
            <AlertCircle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0" />
            <span>Sem Instância</span>
          </div>
        )}
      </div>

      {/* Provider + Migração */}
      <div className="hidden md:flex items-center gap-2 w-32 shrink-0">
        {inbox.provider && (
          <Badge variant="outline" className="text-[10px] bg-muted/50 font-normal">
            via {inbox.provider.display_name || inbox.provider.name}
          </Badge>
        )}
        
        {/* Badge de migração pendente */}
        {hasPendingMigration && migrationInfo && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Badge 
                  variant="outline" 
                  className="text-[10px] bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30 animate-pulse"
                >
                  <ArrowRightLeft className="h-3 w-3 mr-1" />
                  {migrationInfo.new_provider_display_name || migrationInfo.new_provider_slug}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <p>Migração pendente: {migrationInfo.old_provider_display_name || migrationInfo.old_provider_slug} → {migrationInfo.new_provider_display_name || migrationInfo.new_provider_slug}</p>
                {migrationInfo.reason && <p className="text-xs text-muted-foreground">Motivo: {migrationInfo.reason}</p>}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {/* Versão */}
      <div className="hidden lg:flex items-center w-16 shrink-0">
        {inbox.provider_version && (
          <Badge variant="outline" className="text-[10px] font-mono bg-muted/50">
            v{inbox.provider_version.version}
          </Badge>
        )}
      </div>

      {/* Status - apenas ícone colorido + texto em cor padrão (sem badge) */}
      <div className="w-28 shrink-0">
        <div className="flex items-center gap-1.5 text-xs text-foreground">
          <StatusIcon className={cn("h-3.5 w-3.5 shrink-0", statusIconColors[inbox.status] || statusIconColors.pending, statusAnim)} />
          <span className="hidden sm:inline truncate">{statusLabel}</span>
        </div>
      </div>

      {/* Menu de Ações - Responsivo */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8 shrink-0">
            <MoreVertical className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {/* Ações de WhatsApp - permissão de edição */}
          {isWhatsAppNonOfficial && canEditInbox && (
            <>
              {/* Conectar WhatsApp - desabilitado se inativo ou sem instância vinculada */}
              <DropdownMenuItem 
                onClick={() => canExecuteProviderActions && onConnect?.(inbox)}
                disabled={!canExecuteProviderActions}
                className={!canExecuteProviderActions ? 'opacity-50 cursor-not-allowed' : ''}
              >
                <QrCode className="h-4 w-4 mr-2" />
                Conectar WhatsApp
              </DropdownMenuItem>
              {/* Verificar Status - desabilitado se inativo ou sem instância vinculada */}
              <DropdownMenuItem 
                onClick={() => canExecuteProviderActions && onCheckStatus?.(inbox)}
                disabled={!canExecuteProviderActions}
                className={!canExecuteProviderActions ? 'opacity-50 cursor-not-allowed' : ''}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Verificar Status
              </DropdownMenuItem>
              {/* Configurar Webhooks - desabilitado se inativo ou sem instância vinculada */}
              <DropdownMenuItem 
                onClick={() => canExecuteProviderActions && onConfigureWebhooks?.(inbox)}
                disabled={!canExecuteProviderActions}
                className={!canExecuteProviderActions ? 'opacity-50 cursor-not-allowed' : ''}
              >
                <Webhook className="h-4 w-4 mr-2" />
                Configurar Webhooks
              </DropdownMenuItem>
              {/* Desconectar - apenas quando ativo */}
              {inbox.status === 'active' && (
                <DropdownMenuItem onClick={() => onDisconnect?.(inbox)}>
                  <Unplug className="h-4 w-4 mr-2" />
                  Desconectar
                </DropdownMenuItem>
              )}
            </>
          )}
          {/* Ações de WhatsApp Oficial - conexão OAuth */}
          {isWhatsAppOficial && canEditInbox && needsOAuthConnection && onConnectOAuth && (
            <DropdownMenuItem onClick={() => onConnectOAuth(inbox)}>
              <MessageCircle className="h-4 w-4 mr-2" />
              Conectar WhatsApp Business
            </DropdownMenuItem>
          )}
          {/* Reconexão Meta WABA - para inboxes conectadas ou com erro */}
          {isWhatsAppOficial && isMetaWhatsApp && canEditInbox && onConnectOAuth && 
           ['active', 'error', 'disconnected'].includes(inbox.status) && (
            <DropdownMenuItem onClick={() => onConnectOAuth(inbox)}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Reconectar WhatsApp Business
            </DropdownMenuItem>
          )}
          {/* Ações de WebChat - permissão de edição */}
          {isWebchat && canEditInbox && onConfigureWebchat && (
            <DropdownMenuItem onClick={() => onConfigureWebchat(inbox)}>
              <Settings className="h-4 w-4 mr-2" />
              Configurar Widget
            </DropdownMenuItem>
          )}
          
          {/* Separador para ações de instância */}
          {canEditInbox && !isWebchat && (
            <DropdownMenuSeparator />
          )}
          
          {/* Ações de Instância - apenas para canais que requerem (não webchat) */}
          {canEditInbox && !isWebchat && (
            <>
              {hasLinkedInstance ? (
                <DropdownMenuItem 
                  onClick={() => onUnlinkInstance?.(inbox)}
                  className="text-amber-600 dark:text-amber-400"
                >
                  <Unlink className="h-4 w-4 mr-2" />
                  Desvincular Instância
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={() => onLinkInstance?.(inbox)}>
                  <Link2 className="h-4 w-4 mr-2" />
                  Vincular Instância
                </DropdownMenuItem>
              )}
            </>
          )}
          
          <DropdownMenuSeparator />
          
          {/* Ver Detalhes - sempre disponível */}
          <DropdownMenuItem onClick={handleViewDetails}>
            <Eye className="h-4 w-4 mr-2" />
            Ver Detalhes
          </DropdownMenuItem>
          
          {/* Configurações - permissão de edição */}
          {canEditInbox && (
            <DropdownMenuItem onClick={() => onEdit(inbox)}>
              <Settings className="h-4 w-4 mr-2" />
              Configurações
            </DropdownMenuItem>
          )}
          {/* Ativar/Desativar - permissão de edição */}
          {/* Inbox suspensa não pode ser ativada/desativada via toggle */}
          {canEditInbox && !isSuspended && (
            <DropdownMenuItem onClick={() => onToggleStatus(inbox)}>
              {/* Status 'inactive' = inbox inativa → pode ativar */}
              {/* Status 'pending' vem do admin e não deve aparecer aqui (inbox já está bloqueada) */}
              {/* Qualquer outro status (active, connecting, disconnected, error) = inbox ativa → pode desativar */}
              {inbox.status === 'inactive' ? (
                <>
                  <Power className="h-4 w-4 mr-2" />
                  Ativar
                </>
              ) : (
                <>
                  <PowerOff className="h-4 w-4 mr-2" />
                  Desativar
                </>
              )}
            </DropdownMenuItem>
          )}
          {/* Mensagem para inbox suspensa */}
          {canEditInbox && isSuspended && (
            <DropdownMenuItem disabled className="opacity-50 cursor-not-allowed">
              <AlertCircle className="h-4 w-4 mr-2" />
              Suspensa - Faça upgrade
            </DropdownMenuItem>
          )}
          {/* Excluir - permissão de exclusão */}
          {canDeleteInbox && (
            <DropdownMenuItem 
              onClick={() => onDelete(inbox)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Excluir
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
